/**
 * Format detection utilities for selections.
 *
 * Used by mixed-selection toggle logic and refresh accuracy.
 */

const FORMAT_TAG_MAP: Record<string, string> = {
	bold: "STRONG",
	italic: "EM",
	underline: "U",
	strikethrough: "S",
	subscript: "SUB",
	superscript: "SUP",
};

/**
 * Check if a text node is inside a given formatting tag.
 */
const isTextNodeFormatted = (
	textNode: Text,
	tagName: string,
	root: HTMLElement,
): boolean => {
	let node: Node | null = textNode.parentElement;
	while (node && node !== root) {
		if (node instanceof HTMLElement && node.tagName === tagName) return true;
		node = node.parentNode;
	}
	return false;
};

/**
 * Get all text nodes within a Range.
 */
const getTextNodesInRange = (range: Range): Text[] => {
	const textNodes: Text[] = [];
	const root = range.commonAncestorContainer;

	if (root.nodeType === Node.TEXT_NODE) {
		if ((root as Text).textContent?.trim()) {
			textNodes.push(root as Text);
		}
		return textNodes;
	}

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => {
			if (!node.textContent?.trim()) return NodeFilter.FILTER_SKIP;
			const nodeRange = document.createRange();
			nodeRange.selectNode(node);
			if (
				range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0 &&
				range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0
			) {
				return NodeFilter.FILTER_ACCEPT;
			}
			return NodeFilter.FILTER_SKIP;
		},
	});

	let current = walker.nextNode();
	while (current) {
		textNodes.push(current as Text);
		current = walker.nextNode();
	}

	return textNodes;
};

/**
 * Check if ALL text in the current selection has a given format.
 * Returns true only if every non-empty text node in the selection is
 * wrapped in the corresponding formatting tag.
 *
 * For collapsed selections, falls back to checking the parent chain
 * (same as current behavior).
 */
export const isFormatUniform = (
	format: "bold" | "italic" | "underline" | "strikethrough" | "subscript" | "superscript",
	root: HTMLElement,
): boolean => {
	const sel = window.getSelection();
	if (!sel || sel.rangeCount === 0) return false;

	const range = sel.getRangeAt(0);
	const tagName = FORMAT_TAG_MAP[format];
	if (!tagName) return false;

	// Collapsed: check parent chain
	if (range.collapsed) {
		let node: Node | null = range.startContainer;
		while (node && node !== root) {
			if (node instanceof HTMLElement && node.tagName === tagName) return true;
			node = node.parentNode;
		}
		return false;
	}

	// Selection: check all text nodes
	const textNodes = getTextNodesInRange(range);
	if (textNodes.length === 0) return false;

	for (const textNode of textNodes) {
		if (!isTextNodeFormatted(textNode, tagName, root)) return false;
	}

	return true;
};
